import { getPerspectiveMatrix } from "@/core/perspective"
import { radians } from "@/core/utils/math"
import { mat4, vec2, vec3, vec4 } from "gl-matrix"
import { Adjustments, GradientMask, MaskGroup, Mask, RadialMask, Rectangle } from "@/core/types"
import { getCropMatrix } from "@/core/crop"

export function intp(a: number, b: number, c: number): number {
	return a * (b - c) + c
}

export function lerp(a: number, b: number, t: number) {
	return a * (1 - t) + b * t
}

export function clamp(val: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, val))
}

/** distance between points (x, y) and (0, 0) */
/*export const circleDist = (pt: number[], center: number[], r: number) => {
	const lhs = Math.pow(center[0] - pt[0], 2) + Math.pow(center[1] - pt[1], 2)
	const rhs = Math.pow(r, 2)

	return lhs < rhs ? -1 : lhs === rhs ? 0 : 1
}*/

/**
 * javascript's modulo operator doesn't produce positive numbers with negative input
 * https://www.geeksforgeeks.org/how-to-get-negative-result-using-modulo-operator-in-javascript/
 */
export const mod = (a: number, n: number) => ((a % n) + n) % n

/* Math on 2D arrays functions */

/*export const slice2d = (
	array: number[][],
	start: number | undefined = undefined,
	end: number | undefined = undefined,
): number[][] => {
	return array.map((row) => row.slice(start, end))
}

export const map2d = (
	array1: number[][],
	array2: number[][],
	handler: (a1: number, a2: number) => number,
): number[][] => {
	let maxLength = Math.max(array1.length, array2.length)

	let result: number[][] = []

	for (let i = 0; i < maxLength; i++) {
		let row0 = array1[i % array1.length] ?? []
		let row1 = array2[i % array2.length] ?? []

		let maxInnerLength = Math.max(row0.length, row1.length)
		let rowResult: number[] = []

		for (let j = 0; j < maxInnerLength; j++) {
			let val0 = row0[j % row0.length] ?? 0
			let val1 = row1[j % row1.length] ?? 0

			rowResult.push(handler(val0, val1))
		}

		result.push(rowResult)
	}

	return result
}*/

/* Calculating IOU and boxes intersections */

export const areaOf = (box: vec4) => {
	return (box[2] - box[0]) * (box[3] - box[1])
}

export const getIOUs = (box1: vec4, box2: vec4) => {
	const left = Math.max(box1[0], box2[0])
	const top = Math.max(box1[1], box2[1])
	const right = Math.min(box1[2], box2[2])
	const bottom = Math.min(box1[3], box2[3])
	const overlapArea = Math.max(right - left, 0) * Math.max(bottom - top, 0)

	const area1 = areaOf(box1)
	const area2 = areaOf(box2)

	const iou = overlapArea / (area1 + area2 - overlapArea)
	const ioSmall = overlapArea / Math.min(area1, area2)

	return { iou, ioSmall }
}

/*export const areaOf2d = (left_top: number[][], right_bottom: number[][]): number[] => {
	const hw = right_bottom.map((rb, i) => {
		return rb.map((val, j) => {
			return Math.max(val - left_top[i][j], 0.0)
		})
	})
	return hw.map((arr) => arr[0] * arr[1])
}*/

/*export const iouOf = (boxes0: number[][], boxes1: number[][]): number[] => {
	const overlap_left_top = map2d(slice2d(boxes0, 0, 2), slice2d(boxes1, 0, 2), Math.max)
	const overlap_right_bottom = map2d(slice2d(boxes0, -2), slice2d(boxes1, -2), Math.min)

	const overlapArea = areaOf2d(overlap_left_top, overlap_right_bottom)
	const area0 = areaOf2d(slice2d(boxes0, 0, 2), slice2d(boxes0, -2))
	const area1 = areaOf2d(slice2d(boxes1, 0, 2), slice2d(boxes1, -2))
	const eps = 1e-5

	const iou = overlapArea.map((overlapAreaValue, index) => {
		return (
			overlapAreaValue /
			(area0[index] + area1[Math.min(area1.length - 1, index)] - overlapAreaValue + eps)
		)
	})

	return iou
}*/

/*
// The HardNMS function.
export const hardNms = (
    boxScores: number[][], 
    iouThreshold: number, 
    topK: number = -1, 
    candidateSize: number = 200
): number[][] => {
    let scores = boxScores.map(value => value[value.length - 1])
    let boxes = boxScores.map(value => value.slice(0, -1))
    let picked: number[] = []

    let indexes = scores.map((_, index) => index)
        .sort((a, b) => scores[a] - scores[b])
        .slice(-candidateSize)

    while (indexes.length > 0) {
        let current = indexes.pop()

        if (!current) {
            break
        }

        picked.push(current)

        if (0 < topK && topK === picked.length || indexes.length === 1) {
            break
        }
        
        const currentBox = boxes[current] ?? []
        const restBoxes = indexes.map(index => boxes[index] ?? [])
        const iou = iouOf(restBoxes, [currentBox])

        indexes = indexes.filter((_, index) => (iou[index] ?? 0) <= iouThreshold)
    }

    return picked.map(index => boxScores[index] ?? [])
}
*/

// Following are helper functions for crop geometry

export function rotatePoint(x: number, y: number, theta: number, origin: vec2 = [0, 0]): vec2 {
	// Translate point to origin
	x -= origin[0]
	y -= origin[1]

	// Rotate point
	let xNew = x * Math.cos(theta) - y * Math.sin(theta)
	let yNew = x * Math.sin(theta) + y * Math.cos(theta)

	// Translate point back
	xNew += origin[0]
	yNew += origin[1]

	return [xNew, yNew]
}

export function getRotatedCrop(crop, theta, origin) {
	const [left, top, right, bottom] = crop
	const vertices = [
		[left, top],
		[right, top],
		[right, bottom],
		[left, bottom],
	]

	const rotatedVertices: vec2[] = []
	for (const vertice of vertices) {
		const rotated = rotatePoint(vertice[0], vertice[1], theta, origin)
		rotatedVertices.push(rotated)
	}

	return rotatedVertices
}

export function getRectBounds(vertices: vec2[]): [vec2, vec2] {
	let minX = 100,
		minY = 100,
		maxX = -100,
		maxY = -100
	for (const vertice of vertices) {
		minX = Math.min(minX, vertice[0])
		maxX = Math.max(maxX, vertice[0])
		minY = Math.min(minY, vertice[1])
		maxY = Math.max(maxY, vertice[1])
	}

	return [
		[minX, maxX],
		[minY, maxY],
	]
}

// Get the out of image-bound percent of the line from (x,y) to origin (only for un-rotated image)
// Method: use out-of-bounds delta-x, delta-y, and take the maximum percent.
// This method is more stable numerically than directly compute the intersection
export function getOutofBoundsPercent(point, rotatedOrigin, imageBounds) {
	const [x, y] = point
	const [imageBoundX, imageBoundY] = imageBounds

	const scaleLeft = Math.min(0, x - imageBoundX[0]) / (x - rotatedOrigin[0])
	const scaleRight = Math.max(0, x - imageBoundX[1]) / (x - rotatedOrigin[0])
	const scaleTop = Math.min(0, y - imageBoundY[0]) / (y - rotatedOrigin[1])
	const scaleBottom = Math.max(0, y - imageBoundY[1]) / (y - rotatedOrigin[1])

	const sx = Math.max(0, scaleLeft, scaleRight)
	const sy = Math.max(0, scaleTop, scaleBottom)
	const s = Math.max(sx, sy)

	return s
}

export const getFractionalDigits = (val: number) => {
	return val.toString().split(".")[1]?.length ?? 0
}

export const toFixed = (val: number, fractionDigits: number) => {
	const mul = 10 ** fractionDigits
	return Math.round(val * mul) / mul
}

// Following are helper functions for auto straighten and perspective correction

export function distSq(x1: number, y1: number, x2: number, y2: number): number {
	return (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1)
}

export function dist(x1: number, y1: number, x2: number, y2: number): number {
	return Math.sqrt(distSq(x1, y1, x2, y2))
}

export function rad2Degree(rad) {
	return (rad / Math.PI) * 180
}

// Ax + By + C = 0
export function getLineEquation(endPoints: vec4): vec3 {
	const [x1, y1, x2, y2] = endPoints
	const A = y2 - y1
	const B = x1 - x2
	const C = x2 * y1 - x1 * y2
	const scale = Math.sqrt(A * A + B * B)
	return [A / scale, B / scale, C / scale]
}

export function distanceFromPointToLine(point: vec2, lineEq: vec3) {
	const [A, B, C] = lineEq // normalized: Math.sqrt(A * A + B * B) = 1
	const [x, y] = point

	if (A == 0 && B == 0) return -10000

	return Math.abs(A * x + B * y + C) // / Math.sqrt(A * A + B * B);
}

export function getIntersection(lineEq1: vec3, lineEq2: vec3, eps: number = 0.0001): vec2 | null {
	const [A1, B1, C1] = lineEq1
	const [A2, B2, C2] = lineEq2
	const det = A2 * B1 - A1 * B2

	if (Math.abs(det) < eps) {
		// The lines are parallel (or coincide)
		return null
	} else {
		const x = (B2 * C1 - B1 * C2) / det
		const y = (A1 * C2 - A2 * C1) / det
		return [x, y]
	}
}

export function isPerpendicularPointOnSegment(point: vec2, line: vec4) {
	const [x, y] = point
	const [x1, y1, x2, y2] = line
	// Handle the case where the line segment is vertical
	if (Math.abs(x1 - x2) < 0.0001) {
		const minY = Math.min(y1, y2)
		const maxY = Math.max(y1, y2)
		return y >= minY && y <= maxY
	}

	const m = (y2 - y1) / (x2 - x1)
	const c1 = y1 - m * x1 // y intercept of segment

	// Slope of the perpendicular line
	const mPerpendicular = -1 / m
	const cPerpendicular = y - mPerpendicular * x // y intercept of perpendicular line

	// Intersection point
	const xIntersection = (cPerpendicular - c1) / (m - mPerpendicular)
	const yIntersection = m * xIntersection + c1

	// return (xIntersection >= Math.min(x1, x2) && xIntersection <= Math.max(x1, x2))
	//     && (yIntersection >= Math.min(y1, y2) && yIntersection <= Math.max(y1, y2));
	return onSegment([x1, y1], [xIntersection, yIntersection], [x2, y2])
}

// helper function for contraint crop for perspective transform

function onSegment(p: vec2, q: vec2, r: vec2, eps = 0.000001): boolean {
	// Given three collinear points p, q, r, check if point q lies on line segment pr
	return (
		q[0] <= Math.max(p[0], r[0]) + eps &&
		q[0] >= Math.min(p[0], r[0]) - eps &&
		q[1] <= Math.max(p[1], r[1]) + eps &&
		q[1] >= Math.min(p[1], r[1]) - eps
	)
}

function isDiffSmall(a: number, b: number, EPSILON = 0.001) {
	return Math.abs(a - b) < EPSILON
}

export function getOutofBoundsPercentForIrregularRect(
	point: vec2,
	origin: vec2,
	vertices: vec2[],
	checkDirection = false,
) {
	const [x, y] = point as [number, number]
	const dx = x - origin[0]
	const dy = y - origin[1]
	const dl = Math.sqrt(dx * dx + dy * dy)
	const dx0 = dx / dl
	const dy0 = dy / dl

	const lineEq = getLineEquation([x, y, origin[0], origin[1]])
	const lineLength = dist(x, y, origin[0], origin[1])
	if (lineLength == 0) return { maxIndex: -1, maxPercent: 0 }

	// origin must be inside the rect or on the edge
	// if (!pointInIrregularRect(origin, vertices)) {
	// 	console.error("Origin is not in rect:", { origin, vertices })
	// }

	const n = vertices.length // 4

	let maxPercent = 0
	let maxIndex = -1
	for (let i = 0; i < n; i++) {
		const edge = [...(vertices[i] as vec2), ...(vertices[(i + 1) % n] as vec2)] as vec4
		const lineEqEdge = getLineEquation(edge)

		// Find the intersection of the line point-origin with edges
		const intersection = getIntersection(lineEq, lineEqEdge)
		if (intersection) {
			const [sx, sy] = intersection

			// Safe guard for precision issue that origin might be slightly outside of bounds
			if (isDiffSmall(sx, origin[0]) && isDiffSmall(sy, origin[1])) {
				if (checkDirection) {
					const x0 = sx + dx0 * 0.03
					const y0 = sy + dy0 * 0.03
					if (pointInIrregularRect([x0, y0], vertices)) {
						continue
					}
				} else {
					continue
				}
			} else if (
				!onSegment([edge[0], edge[1]], intersection, [edge[2], edge[3]]) ||
				!onSegment(point, intersection, origin)
			) {
				continue
			}

			const scale = dist(x, y, sx, sy) / lineLength
			if (scale > maxPercent + 0.000001) {
				maxPercent = scale
				maxIndex = i
			}
		}
	}

	// Try to prevent numerical precision issue: make sure adjusted crop is truly within bounds
	if (maxPercent != 0) maxPercent = Math.min(maxPercent + 0.0001, 1)

	return { maxIndex, maxPercent }
}

export function getTransformationMatrix(
	adjustments: Adjustments,
	orientedSize: vec2,
	initialOrientation: number,
) {
	const [ow, oh] = orientedSize
	const orientation = adjustments.orientation + initialOrientation
	const [iw, ih] = orientation % 2 ? [oh, ow] : [ow, oh]

	// Perspective matrix
	const mat = getPerspectiveMatrix(adjustments, initialOrientation, [iw, ih])

	// Crop matrix
	const mat2 = mat4.create()
	getCropMatrix(
		mat2,
		orientedSize,
		[0, 0, 1, 1],
		adjustments.cropStraighten,
		adjustments.flipX,
		adjustments.flipY,
		orientation,
	)
	mat4.invert(mat2, mat2)

	const combined = mat4.create()
	mat4.multiply(combined, mat, mat2)

	return combined
}

export function getTransformedImageVertices(
	adjustments: Adjustments,
	orientedSize: vec2,
	initialOrientation: number,
) {
	const mat = getTransformationMatrix(adjustments, orientedSize, initialOrientation ?? 0)

	const inverseMatrix = mat4.create()
	if (mat4.invert(inverseMatrix, mat) === null) {
		console.error("Matrix is not invertible!")
	}

	const hwRatio = orientedSize[1] / orientedSize[0]
	const imageVertices: vec2[] = [
		[0, 0],
		[1, 0],
		[1, hwRatio],
		[0, hwRatio],
	]

	return transformCoords(imageVertices, inverseMatrix, orientedSize, false)
}

// input/output ranges [0, 1],[0, hwRatio]
export function transformCoords(
	vertices: vec2[],
	matrix: mat4,
	orientedSize: vec2,
	forward: boolean,
) {
	const transformedVertices: vec2[] = []
	const hwRatio = orientedSize[1] / orientedSize[0]
	for (const vertice of vertices) {
		const resultVector = vec4.create()
		let [xn, yn] = vertice
		// convert from [0, 1],[0, hwRatio] to [-1, 1],[-1, 1] (not [-1, 1],[1, -1])
		xn = 2 * xn - 1
		yn = (2 * yn) / hwRatio - 1

		if (forward) {
			// output -> input coord, same as shader
			const vector: vec4 = [xn, yn, 0, 1]
			vec4.transformMat4(resultVector, vector, matrix)
			const zFactor = 1 + resultVector[2]
			resultVector[0] /= zFactor
			resultVector[1] /= zFactor
		} else {
			const a = matrix[2]
			const b = matrix[6]
			const c = matrix[10]
			const outputZ = 0 // should be 1 in theory
			const zi = (outputZ - a * xn - b * yn) / (c + a * xn + b * yn)
			const vector: vec4 = [xn * (1 + zi), yn * (1 + zi), zi, 1]
			vec4.transformMat4(resultVector, vector, matrix) // resultVector = [x, y, outputZ, 1]
		}
		// convert back to [0, 1],[0, hwRatio]
		const x = resultVector[0] * 0.5 + 0.5
		const y = (resultVector[1] * 0.5 + 0.5) * hwRatio

		transformedVertices.push([x, y])
	}

	return transformedVertices
}

function getOrderedCoords(vertices: vec2[]): [vec4, vec4] {
	const xs = vertices.map((val) => val[0]).sort() as vec4
	const ys = vertices.map((val) => val[1]).sort() as vec4
	return [xs, ys]
}

// Get approximate overlap center between vertices and rotated rect by rotationAngle
export function getOverlapCenter(vertices: vec2[], rect: vec4, rotationAngle: number) {
	const [left, top, right, bottom] = rect // this is unrotated rect

	// Rotate vertices back by -rotationAngle to compute overlap center for better stability
	const origin: vec2 = [(left + right) / 2, (top + bottom) / 2]
	const rotatedVertices: vec2[] = []
	for (const vertice of vertices) {
		const rotated = rotatePoint(vertice[0], vertice[1], -rotationAngle, origin)
		rotatedVertices.push(rotated)
	}
	const [xs1, ys1] = getOrderedCoords(rotatedVertices)
	const left1 = Math.max(xs1[0], xs1[1])
	const right1 = Math.min(xs1[2], xs1[3])
	const top1 = Math.max(ys1[0], ys1[1])
	const bottom1 = Math.min(ys1[2], ys1[3])

	const leftOverlap = Math.max(left1, left)
	const rightOverlap = Math.min(right1, right)
	const topOverlap = Math.max(top1, top)
	const bottomOverlap = Math.min(bottom1, bottom)

	let xCenter = (leftOverlap + rightOverlap) / 2
	let yCenter = (topOverlap + bottomOverlap) / 2
	const eps = 0.02
	xCenter = clamp(xCenter, left + eps, right - eps)
	yCenter = clamp(yCenter, top + eps, bottom - eps)

	// rotate by the original angle to get correct coord
	const center = rotatePoint(xCenter, yCenter, rotationAngle, origin)

	// if (!pointInIrregularRect(center, vertices)) {
	// 	console.error("center not in vertices:", center, vertices)
	// }

	return center
}

function pointInTriangle(P: vec2, A: vec2, B: vec2, C: vec2) {
	const detT = (B[1] - C[1]) * (A[0] - C[0]) + (C[0] - B[0]) * (A[1] - C[1])

	const alpha = ((B[1] - C[1]) * (P[0] - C[0]) + (C[0] - B[0]) * (P[1] - C[1])) / detT
	const beta = ((C[1] - A[1]) * (P[0] - C[0]) + (A[0] - C[0]) * (P[1] - C[1])) / detT
	const gamma = 1 - alpha - beta

	return alpha >= 0 && alpha <= 1 && beta >= 0 && beta <= 1 && gamma >= 0 && gamma <= 1
}

function pointInIrregularRect(P: vec2, irregularRect: vec2[]) {
	const [v1, v2, v3, v4] = irregularRect as [vec2, vec2, vec2, vec2]
	// Check if P is inside triangle formed by v1, v2, v3 or v1, v3, v4
	return pointInTriangle(P, v1, v2, v3) || pointInTriangle(P, v1, v3, v4)
}

export function getLimitedDxDyForRotation(
	dx: number,
	dy: number,
	crop: vec4,
	orientedSize: vec2,
	rotationRadian: number,
) {
	const hwRatio = orientedSize[1] / orientedSize[0]

	const transformedCrop: vec2[] = getRotatedCrop(crop, -rotationRadian, [0.5, 0.5 * hwRatio])

	const [[minX, maxX], [minY, maxY]] = getRectBounds(transformedCrop)

	const imageBoundX: vec2 = [0, 1]
	const imageBoundY: vec2 = [0, hwRatio]

	const limitDx: vec2 = [imageBoundX[0] - minX, imageBoundX[1] - maxX]
	const limitDy: vec2 = [imageBoundY[0] - minY, imageBoundY[1] - maxY]

	let [rdx, rdy] = rotatePoint(dx, dy, -rotationRadian, [0, 0])

	// limit the transformed dx, dy by distance between imageBounds and transformed cropBounds
	rdx = clamp(rdx, limitDx[0], limitDx[1])
	rdy = clamp(rdy, limitDy[0], limitDy[1])

	return rotatePoint(rdx, rdy, rotationRadian, [0, 0])
}

export function getLimitedDxDyWithSlidingEdge(
	crop: vec4,
	dx: number,
	dy: number,
	vertices1: vec2[],
	vertices2: vec2[],
): vec2 {
	const n = vertices1.length // 4
	const edges: vec4[] = [
		...vertices1.map(
			(value, index, array) => [...value, ...(array[(index + 1) % n] as vec2)] as vec4,
		),
		...vertices2.map(
			(value, index, array) => [...value, ...(array[(index + 1) % n] as vec2)] as vec4,
		),
	]
	const [left, top, right, bottom] = crop as [number, number, number, number]
	const cropVertices: vec2[] = [
		[left, top],
		[right, top],
		[right, bottom],
		[left, bottom],
	]

	// Find the first collision point
	let collideIndex = -1
	let collidePercent = 0
	for (const point of cropVertices) {
		const { maxIndex, maxPercent } = getOutofBoundsPercentForTwoShapes(
			point,
			dx,
			dy,
			vertices1,
			vertices2,
		)
		if (maxPercent > collidePercent + 0.000001) {
			collidePercent = maxPercent
			collideIndex = maxIndex
		}
	}

	if (collideIndex === -1) return [dx, dy]

	// Slide along the first collision edge
	const collideEdge = edges[collideIndex] as vec4
	let newDx = dx * (1 - collidePercent)
	let newDy = dy * (1 - collidePercent)

	const [dx2, dy2] = projection(dx * collidePercent, dy * collidePercent, collideEdge) as [
		number,
		number,
	]

	let collidePercent2 = 0
	for (const point of cropVertices) {
		const newPoint: vec2 = [point[0] + newDx, point[1] + newDy]
		const res = getOutofBoundsPercentForTwoShapes(newPoint, dx2, dy2, vertices1, vertices2)
		if (res.maxPercent > collidePercent2 + 0.000001) {
			collidePercent2 = res.maxPercent
		}
	}

	newDx += dx2 * (1 - collidePercent2)
	newDy += dy2 * (1 - collidePercent2)

	return [newDx, newDy]
}

function projection(dx: number, dy: number, line: vec4): vec2 {
	const [x1, y1, x2, y2] = line
	const L: vec2 = [x2 - x1, y2 - y1]

	const magnitudeL = Math.sqrt(L[0] * L[0] + L[1] * L[1])
	const normalizedL: vec2 = [L[0] / magnitudeL, L[1] / magnitudeL]

	const dotProduct = dx * normalizedL[0] + dy * normalizedL[1]

	// Calculate the projection
	const proj: vec2 = [dotProduct * normalizedL[0], dotProduct * normalizedL[1]]

	return proj
}

function getOutofBoundsPercentForTwoShapes(
	origin: vec2,
	dx: number,
	dy: number,
	vertices1: vec2[],
	vertices2: vec2[],
) {
	let maxPercent = 0
	let maxIndex = -1
	const point: vec2 = [origin[0] + dx, origin[1] + dy]
	const res1 = getOutofBoundsPercentForIrregularRect(point, origin, vertices1, true)
	const res2 = getOutofBoundsPercentForIrregularRect(point, origin, vertices2, true)
	if (res1.maxPercent >= res2.maxPercent) {
		maxPercent = res1.maxPercent
		maxIndex = res1.maxIndex
	} else {
		maxPercent = res2.maxPercent
		maxIndex = res2.maxIndex === -1 ? -1 : res2.maxIndex + 4
	}

	return { maxIndex, maxPercent }
}

// for paste: from on-screen cropped image coord to inputTexture coord
// for copy: from inputTexture coord to on-screen cropped image coord
export function transformMaskForCopyPaste(
	mask: RadialMask | GradientMask,
	inputTextureSize: vec2,
	adjustments: Adjustments,
	initialOrientation: number,
	forCopy: boolean,
) {
	const finalOrientation = initialOrientation + adjustments.orientation

	const [width, height] = inputTextureSize
	const imageSize = vec2.create()
	if (Math.abs(finalOrientation) % 2) {
		vec2.set(imageSize, height, width)
	} else {
		vec2.set(imageSize, width, height)
	}

	const cropMatrix = mat4.create()
	const { crop, cropStraighten, flipX, flipY } = adjustments
	getCropMatrix(cropMatrix, imageSize, crop, cropStraighten, flipX, flipY, finalOrientation)

	const cropMatrixInv = mat4.create()
	if (mat4.invert(cropMatrixInv, cropMatrix) === null) {
		console.error("Matrix is not invertible!")
	}

	const cropppedSize: vec2 = [imageSize[0] * crop[2], imageSize[1] * crop[3]]
	const inputSize = forCopy ? inputTextureSize : cropppedSize
	const outputSize = forCopy ? cropppedSize : inputTextureSize
	const matrix = forCopy ? cropMatrix : cropMatrixInv

	if (mask.type === "radial")
		return transformRadialMask(mask, matrix, inputSize, outputSize, finalOrientation, forCopy)
	else return transformGradientMask(mask, matrix, forCopy)
}

export function transformRadialMask(
	mask: RadialMask,
	matrix: mat4,
	inputSize: vec2,
	outputSize: vec2,
	finalOrientation: number,
	forCopy: boolean = true,
) {
	const normalizedVertices = convertCenterCoordToCorners(mask, inputSize)

	const transformedVertices = transformCoords(normalizedVertices, matrix, [1, 1], !forCopy)

	const coord = convertCornersToCenterCoord(
		transformedVertices,
		outputSize,
		forCopy,
		finalOrientation,
	)

	// console.log({swapXY, rotatedVertices, normalizedVertices, transformedVertices, actualVertices, coord})

	const newMask = structuredClone(mask)
	newMask.centerX = coord.normalizedCenterX
	newMask.centerY = coord.normalizedCenterY
	newMask.radiusX = coord.normalizedRadiusX
	newMask.radiusY = coord.normalizedRadiusY
	newMask.angle = coord.angle

	return newMask
}

function convertCenterCoordToCorners(mask: RadialMask, inputSize: vec2): vec2[] {
	const [w, h] = inputSize
	const left = (mask.centerX - mask.radiusX) * w
	const top = (mask.centerY - mask.radiusY) * h
	const right = (mask.centerX + mask.radiusX) * w
	const bottom = (mask.centerY + mask.radiusY) * h
	const origin: vec2 = [mask.centerX * w, mask.centerY * h]
	const rotationRadian = radians(mask.angle)
	const vertices: vec2[] = [
		[left, top],
		[right, top],
		[right, bottom],
		[left, bottom],
	]
	// mask angle positive -> rotate ccw
	const rotatedVertices = vertices.map((val) =>
		rotatePoint(val[0], val[1], -rotationRadian, origin),
	)
	const normalizedVertices: vec2[] = rotatedVertices.map((val) => [val[0] / w, val[1] / h])
	return normalizedVertices
}

// corners x, y is in [0, 1]
function convertCornersToCenterCoord(
	corners: vec2[],
	imageSize: vec2,
	swapXYForAngle: boolean = true,
	finalOrientation: number = 0,
) {
	const [ow, oh] = imageSize as [number, number]

	const actualVertices: vec2[] = corners.map((val) => [val[0] * ow, val[1] * oh])

	const [leftTop, rightTop, rightBottom, leftBottom] = actualVertices as [vec2, vec2, vec2, vec2]

	const centerX = (leftTop[0] + rightBottom[0]) / 2
	const centerY = (leftTop[1] + rightBottom[1]) / 2
	const radiusX = dist(leftTop[0], leftTop[1], rightTop[0], rightTop[1]) / 2
	const radiusY = dist(leftTop[0], leftTop[1], leftBottom[0], leftBottom[1]) / 2

	const dy = rightTop[1] - leftTop[1]
	const dx = rightTop[0] - leftTop[0]
	let angle = rad2Degree(Math.atan2(-dy, dx)) // [-180, 180]

	if (angle < 0) angle += 180 // [0, 180]

	let swapXY = Math.abs(finalOrientation) % 2 !== 0
	if (swapXYForAngle) {
		swapXY = Math.abs(angle - 90) < 45
	}

	angle = swapXY ? angle - 90 : angle
	if (angle > 90) angle -= 180

	return {
		normalizedCenterX: centerX / ow,
		normalizedCenterY: centerY / oh,
		normalizedRadiusX: (swapXY ? radiusY : radiusX) / ow,
		normalizedRadiusY: (swapXY ? radiusX : radiusY) / oh,
		angle: angle,
	}
}

export function transformGradientMask(mask: GradientMask, matrix: mat4, forCopy: boolean = true) {
	const vertices: vec2[] = [
		[mask.startX, mask.startY],
		[mask.endX, mask.endY],
	]

	const [start, end] = transformCoords(vertices, matrix, [1, 1], !forCopy)

	const newMask = structuredClone(mask)
	if (start) {
		newMask.startX = start[0]
		newMask.startY = start[1]
	}
	if (end) {
		newMask.endX = end[0]
		newMask.endY = end[1]
	}

	return newMask
}

export function transformLocalAdjustments(
	localAdjustments: MaskGroup[],
	adjustments: Adjustments,
	originalImageSize: vec2,
	initialOrientation: number,
	forCopy: boolean,
) {
	const newLocalAdjustments: MaskGroup[] = []
	for (const originalLocal of localAdjustments) {
		const localAdjustment = transformLocalAdjustment(
			originalLocal,
			adjustments,
			originalImageSize,
			initialOrientation,
			forCopy,
		)
		newLocalAdjustments.push(localAdjustment)
	}
	return newLocalAdjustments
}

export function transformLocalAdjustment(
	originalLocalAdjustment: MaskGroup,
	adjustments: Adjustments,
	originalImageSize: vec2,
	initialOrientation: number,
	forCopy: boolean,
) {
	const localAdjustment = { ...originalLocalAdjustment }
	const masks: Mask[] = localAdjustment.masks.map((mask) => {
		return mask.type === "gradient" || mask.type === "radial"
			? transformMaskForCopyPaste(
					mask,
					originalImageSize,
					adjustments,
					initialOrientation,
					forCopy,
				)
			: mask
	})
	localAdjustment.masks = masks

	return localAdjustment
}

export function equals(a?: number, b?: number, eps = 1e-5): boolean {
	if (a !== undefined && b !== undefined) {
		return Math.abs(a - b) < eps
	} else if (a === undefined && b === undefined) {
		return true
	} else {
		return false
	}
}

export function rectEquals(a: Rectangle, b: Rectangle): boolean {
	return (
		equals(a.x, b.x) &&
		equals(a.y, b.y) &&
		equals(a.width, b.width) &&
		equals(a.height, b.height)
	)
}

export function rectSizeEquals(a: Rectangle, b: Rectangle): boolean {
	return equals(a.width, b.width) && equals(a.height, b.height)
}

export function rectIn(x: number, y: number, rc: Rectangle): boolean {
	return x > rc.x && x < rc.x + rc.width && y > rc.y && y < rc.y + rc.height
}

export function rectOverlap(a: Rectangle, b: Rectangle): boolean {
	return (
		rectIn(b.x, b.y, a) ||
		rectIn(a.x, a.y, b) ||
		rectIn(b.x + b.width, b.y, a) ||
		rectIn(a.x + a.width, a.y, b) ||
		rectIn(b.x, b.y + b.height, a) ||
		rectIn(a.x, a.y + a.height, b) ||
		rectIn(b.x + b.width, b.y + b.height, a) ||
		rectIn(a.x + a.width, a.y + a.height, b)
	)
}

export function rectPadding(rc: Rectangle, padWidth: number, padHeight?: number): Rectangle {
	const [padW, padH] = [padWidth, undefined === padHeight ? padWidth : padHeight]
	const x = Math.max(rc.x - padW, 0)
	const y = Math.max(rc.y - padH, 0)
	return {
		x,
		y,
		width: rc.x + rc.width + padW - x,
		height: rc.y + rc.height + padH - y,
	}
}

export function rectOffset(rc: Rectangle, deltaX: number, deltaY: number): Rectangle {
	return { ...rc, x: rc.x + deltaX, y: rc.y + deltaY }
}

export function rectClamp(rc: Rectangle, min: number, max: number): Rectangle {
	let [r, b] = [rc.x + rc.width, rc.y + rc.height]
	r = clamp(r, min, max)
	b = clamp(b, min, max)
	const x = clamp(rc.x, min, max)
	const y = clamp(rc.y, min, max)
	return { x, y, width: r - x, height: b - y }
}

export function isVec2InRange(vector: vec2, min: number, max: number): boolean {
	return vector[0] >= min && vector[0] <= max && vector[1] >= min && vector[1] <= max
}

export function rectCenter(rc: Rectangle): vec2 {
	return [rc.x + rc.width / 2, rc.y + rc.height / 2]
}

export function l2Distance(
	vector1: number[] | Float32Array | undefined,
	vector2: number[] | Float32Array | undefined,
	maxDist = 2,
) {
	if (vector1 == null) {
		vector1 = []
	}
	if (vector2 == null) {
		vector2 = []
	}
	if (vector1.length !== vector2.length) {
		return maxDist // max distance
	}

	let sumOfSquares = 0
	for (let i = 0; i < vector1.length; i++) {
		const difference = (vector1[i] as number) - (vector2[i] as number)
		sumOfSquares += difference * difference
	}

	return Math.sqrt(sumOfSquares)
}

export const normalizeValue = (value: number | number[], validRange: [number, number]) => {
	const [min, max] = validRange
	if (Array.isArray(value)) {
		return value.map((v) => (v - min) / (max - min))
	} else {
		return (value - min) / (max - min)
	}
}
export const denormalizeValue = (
	normalizedValue: number | number[],
	validRange: [number, number],
) => {
	const [min, max] = validRange
	if (Array.isArray(normalizedValue)) {
		return normalizedValue.map((v) => v * (max - min) + min)
	} else {
		return normalizedValue * (max - min) + min
	}
}

// Helper function to find the Greatest Common Divisor (GCD)
function gcd(a: number, b: number): number {
	return b === 0 ? a : gcd(b, a % b)
}

// Helper function to convert decimal to fraction
export function decimalToFraction(decimal: number): string {
	// Choose a denominator for precision (e.g., 1000000 for six decimal places)
	const denominator = 1000000
	const numerator = Math.round(decimal * denominator)

	// Simplify the fraction
	const divisor = gcd(numerator, denominator)

	const simplifiedNumerator = numerator / divisor
	const simplifiedDenominator = denominator / divisor

	return `${simplifiedNumerator}/${simplifiedDenominator}`
}
